/**
 * <refund-all-orders> — every `paid` order of the mounted event, refunded in
 * one batch. Opened from the console's Orders destination, where the orders it
 * acts on are the list the operator is already looking at.
 *
 * A mass refund, and nothing beyond one. The event stays `published` — the
 * model carries no cancelled state for an event and nothing here writes the
 * event document at all — so every seat this batch releases is buyable again
 * the moment it comes back. That is exactly what the seats line says, and the
 * copy states the rest plainly rather than leaving it to be inferred from a
 * name: an operator who means to call the show off must archive the event as
 * well, which is a separate act on a separate surface.
 *
 * No ticket selection at all, and that is the difference from the per-order
 * step in order-detail.view.ts rather than an omission: a whole-event refund
 * owes every buyer their whole order back, so each selection entry names its
 * order alone and the consumer takes every ticket of it. The one thing that
 * survives is an admission already recorded at the door — a refund that
 * rewrote it would erase the only record of who came in — so an order holding
 * a redeemed ticket has its marker refused whole, and this surface says how
 * many of those there are, and leaves their money and their seats out of the
 * figures above, before the operator commits rather than after.
 *
 * A decision view: the question leads, the three consequences follow with
 * their real figures, and the action sits under them. Every one of those
 * figures is derived from the event's tickets, which ride a different
 * permission from the orders beside them — so a session that cannot list them
 * gets the refusal in place of the decision, never three zeros and a live
 * button.
 *
 * Once the batch is sent the same surface becomes its progress readout, and it
 * reads only its OWN batch: the outcome cell is module-scoped and outlives any
 * one overlay, so this view renders progress only after its own send, and the
 * `.sync` resets that cell at the top of every request. Sending and refusal
 * are states of this surface too — a create the rules refuse leaves an
 * operator on a progress screen forever otherwise.
 *
 * Zero-I/O, like every view here: the confirm fires `refundBatchRequested$`
 * and `web`'s order-ops.sync.ts mints the batch id, creates the
 * document, and answers on `refundBatchOutcome`.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type TemplateResult } from "lit";
import { state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../../i18n/localize.ts";
import { consoleNotLive } from "../console.state.ts";
import { formatCents } from "../order-detail.engine.ts";
import {
	eventRefundDraft,
	refundBatchOutcome,
	refundBatchRequested$,
} from "../order-ops.state.ts";

@schmancyElement("refund-all-orders")
export class RefundAllOrders extends LocalizeMixin(SchmancyElement) {
	/** Whether THIS mounting has already sent its batch — the one thing the
	 *  module-scoped outcome cell cannot say, since it survives every overlay
	 *  that ever read it. A fresh open shows the decision again. */
	@state() private sent = false;

	override render(): TemplateResult {
		const outcome = refundBatchOutcome.value;
		if (this.sent && outcome?.kind === "accepted") {
			const answered = outcome.done + outcome.failed;
			return html`
				<div class="flex w-md max-w-full flex-col gap-3 p-6">
					<schmancy-progress
						.value=${answered}
						.max=${outcome.orderCount}
						aria-label=${this.t("admin.events.console.refundAllOrders.progressAria")}
					></schmancy-progress>
					<schmancy-typography type="body" token="sm">
						${this.t("admin.events.console.refundAllOrders.progressLabel", {
							done: answered,
							count: outcome.orderCount,
						})}
					</schmancy-typography>
					${when(
						outcome.failed > 0,
						() => html`
							<schmancy-typography type="body" token="sm" class="text-error-default">
								${this.t(
									"admin.events.console.refundAllOrders.progressFailed",
									{
										count: outcome.failed,
									},
								)}
							</schmancy-typography>
						`,
					)}
					${when(
						!outcome.watched,
						() => html`
							<schmancy-typography type="body" token="sm" class="text-warning-default">
								${this.t("admin.events.console.refundAllOrders.progressUnwatched")}
							</schmancy-typography>
						`,
					)}
					${when(
						outcome.watched && answered >= outcome.orderCount,
						() => html`
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t("admin.events.console.refundAllOrders.progressSettled")}
							</schmancy-typography>
						`,
					)}
				</div>
			`;
		}

		const draft = eventRefundDraft.get();
		if (!draft) return html``;

		if (draft.kind === "ticketsUnreadable")
			return html`
				<div class="flex w-md max-w-full flex-col gap-4 p-6">
					<schmancy-typography type="headline" token="sm">
						${this.t("admin.events.console.refundAllOrders.title", {
							count: draft.orderCount,
						})}
					</schmancy-typography>
					<schmancy-typography type="body" token="md" class="text-error-default">
						${this.t("admin.events.console.refundAllOrders.ticketsUnreadable")}
					</schmancy-typography>
					<div class="flex items-center justify-end">
						<schmancy-button
							variant="outlined"
							color="neutral"
							@click=${() => this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }))}
							>${this.t("admin.common.cancel")}</schmancy-button
						>
					</div>
				</div>
			`;

		return html`
			<div class="flex w-md max-w-full flex-col gap-4 p-6">
				<schmancy-typography type="headline" token="sm">
					${this.t("admin.events.console.refundAllOrders.title", {
						count: draft.orderCount,
					})}
				</schmancy-typography>

				${when(consoleNotLive.get(), () => html`<schmancy-typography role="status" type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.notLive")}</schmancy-typography>`)}
				<div class="flex flex-col gap-1">
					<schmancy-typography type="body" token="md">
						${this.t("admin.events.console.refundAllOrders.moneyLine", {
							amount: formatCents(draft.totalCents),
						})}
					</schmancy-typography>
					<schmancy-typography type="body" token="md">
						${this.t("admin.events.console.refundAllOrders.ticketsLine", {
							count: draft.ticketCount,
						})}
					</schmancy-typography>
					<schmancy-typography type="body" token="md">
						${this.t("admin.events.console.refundAllOrders.seatsLine", {
							count: draft.ticketCount,
						})}
					</schmancy-typography>
				</div>

				<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
					${this.t("admin.events.console.refundAllOrders.staysPublished")}
				</schmancy-typography>

				${when(
					draft.redeemedOrders > 0,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-warning-default">
							${this.t("admin.events.console.refundAllOrders.redeemedWarning", {
								count: draft.redeemedOrders,
							})}
						</schmancy-typography>
					`,
				)}

				<div class="flex items-center justify-end gap-2">
					<schmancy-button
						variant="outlined"
						color="neutral"
						@click=${() => this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }))}
						>${this.t("admin.common.cancel")}</schmancy-button
					>
					<schmancy-button
						variant="filled"
						color="error"
						.busy=${this.sent && outcome?.kind !== "refused"}

                        @click=${() => {
													if (
														this.sent &&
														refundBatchOutcome.value?.kind !== "refused"
													)
														return;
													this.sent = true;
													refundBatchRequested$.next(draft.selection);
												}}
					>
						${this.t("admin.events.console.refundAllOrders.submit", {
							count: draft.orderCount,
						})}
					</schmancy-button>
				</div>
                <schmancy-typography role="status" type="body" token="sm" class="h-12 text-error-default">
                    ${this.sent && outcome?.kind === "refused" ? this.t("admin.events.console.refundAllOrders.failed") : ""}
                </schmancy-typography>
            </div>
        `;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"refund-all-orders": RefundAllOrders;
	}
}
